// Auth state for the thin-client viewer.
//
// Three-state machine (Pass 2.4 fix for the reload-logout bug):
//   "loading"          — initial mount with a stored token; we
//                        haven't yet confirmed the token still
//                        works against the service. Top-level
//                        routing renders a splash so deep-tree
//                        useEffects DO NOT fire fetches before
//                        the token is settled.
//   "authenticated"    — token validated (or optimistic-auth on
//                        network error during validation).
//   "unauthenticated"  — no token, or 401 from validation.
//
// Pass 2.8: the stored value transitioned from a plain bearer
// string to a JSON object {token, expires_at}. expires_at lets
// us reject stale tokens at boot WITHOUT a server round-trip,
// avoiding the rate of "log in, leave laptop overnight, come
// back, get a 401 storm before the splash even resolves" UX
// pain. Old plain-string values from pre-2.8 are detected on
// rehydrate and forced through a re-login.
//
// The Pass 2.2/2.3.x bug: there were only two states. On reload,
// useState initialized synchronously with the stored token, but
// the API client's _token was set in a useEffect — which fires
// AFTER children's effects per React's bottom-up effect order.
// So the FIRST useWorkspaces() fetch went out with NO bearer
// header, returned 401, the onUnauthorized handler fired
// AuthContext.logout, and the user landed on /login despite a
// valid stored token.
//
// Fix shape:
//   1. Set the API client's token SYNCHRONOUSLY in the useState
//      initializer (during render, not in a useEffect) — by the
//      time any child mounts, lib/api has the bearer.
//   2. Stay in "loading" until validation completes; top-level
//      routing renders <SplashScreen> in that state. No deep-
//      tree fetches fire.
//   3. Validate via a cheap API call (GET /workspaces — empty
//      list for fresh deploys, 401 for stale tokens).
//   4. Network error during validation → optimistic auth +
//      serviceReachable=false. Rationale: a transient network
//      blip shouldn't punish the user with re-login when
//      they're holding what was a valid token a moment ago.
//      Banner surfaces the reachability problem; user can log
//      out manually if they want.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  ApiError,
  listWorkspaces,
  setApiToken,
  setUnauthorizedHandler,
} from "../lib/api";

const STORAGE_KEY = "slack-archive-viewer:token";

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

/**
 * Pass 2.8: shape persisted in localStorage. Token is the JWT
 * the service issued; expires_at is its exp claim mirrored as
 * an ISO 8601 string (so we can reject stale tokens without
 * decoding the JWT body).
 */
export interface StoredAuth {
  token: string;
  expires_at: string;
}

interface AuthState {
  status: AuthStatus;
  /** The bearer token, or null when not authenticated. */
  token: string | null;
  /** ISO 8601 expiry from the JWT's exp claim, or null when not
   *  authenticated. Components can use this to drive proactive
   *  re-login UX (warn near expiry). */
  expiresAt: string | null;
  /** Derived: true while status is "authenticated" (or "loading"
   *  with a stored token). RequireAuth uses status directly; this
   *  is kept for one-line consumers. */
  isAuthenticated: boolean;
  /** Phase 6.1: the role claim decoded from the JWT body
   *  ("superadmin" | "user"), or null when not authenticated /
   *  undecodable. Drives role-gated UI (the Chooser's User-
   *  management tile, the /users route guard). The server is
   *  STILL authoritative — every privileged endpoint re-checks
   *  role server-side; this is purely for hiding UI the user
   *  can't use anyway. */
  role: string | null;
  /** Phase 6.1: the user_id claim from the JWT, or null. Used
   *  by the /users page to identify the self-row ("(you)"). */
  userId: string | null;
  /** True when the most recent token-validation API call succeeded.
   *  False when validation hit a network error and we fell back to
   *  optimistic auth. Components can show a "service unreachable"
   *  banner when false. */
  serviceReachable: boolean;
  /** Save a token and transition to authenticated. Called by the
   *  login screen after a successful POST /auth/login. */
  login: (auth: StoredAuth) => void;
  /** Clear the token and transition to unauthenticated. Called by
   *  the user (sign out) AND automatically by the API client's
   *  401 handler. */
  logout: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/**
 * Read the stored auth object at boot. Returns null when:
 *   - localStorage is unavailable (SSR, private mode)
 *   - nothing is stored
 *   - the stored value is the OLD pre-2.8 plain string (forces
 *     a one-time re-login on first load post-deploy)
 *   - the stored expires_at is in the past (proactive expiry
 *     check; no server round-trip needed to know it's stale)
 *
 * Side-effects on rejection: removes the stale storage entry so
 * subsequent boots aren't paying the parse-and-reject cost.
 */
/** Phase 6.1: decode the JWT body (NOT verifying the signature —
 *  the server does that on every request; this is read-only UI
 *  gating). Returns the parsed claims object, or null if the
 *  token is malformed / undecodable. */
export function decodeJwtClaims(
  token: string | null,
): Record<string, unknown> | null {
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length !== 3) return null;
  try {
    let b = parts[1]!.replace(/-/g, "+").replace(/_/g, "/");
    b += "=".repeat((-b.length % 4 + 4) % 4);
    return JSON.parse(atob(b));
  } catch {
    return null;
  }
}

export function readStoredAuth(): StoredAuth | null {
  if (typeof localStorage === "undefined") return null;
  let raw: string | null;
  try {
    raw = localStorage.getItem(STORAGE_KEY);
  } catch {
    // Private mode / disabled storage. Fall back to memory-only.
    return null;
  }
  if (raw === null) return null;
  // Pass 2.8 backward-compat: pre-2.8 the stored value was a
  // bare bearer-token string, not JSON. Detect and clear.
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    // Not JSON → must be the old plain-string format. Clear and
    // force a re-login. This branch only fires once per browser
    // post-2.8 deploy.
    safeRemove();
    return null;
  }
  if (
    typeof parsed !== "object" ||
    parsed === null ||
    typeof (parsed as { token?: unknown }).token !== "string" ||
    typeof (parsed as { expires_at?: unknown }).expires_at !== "string"
  ) {
    safeRemove();
    return null;
  }
  const auth = parsed as StoredAuth;
  // Proactive expiry check. If the JWT exp is already past,
  // there's no point sending it to the service for a
  // round-trip 401.
  const expiryMs = Date.parse(auth.expires_at);
  if (Number.isNaN(expiryMs) || expiryMs <= Date.now()) {
    safeRemove();
    return null;
  }
  return auth;
}

function writeStoredAuth(auth: StoredAuth | null): void {
  if (typeof localStorage === "undefined") return;
  try {
    if (auth === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
    }
  } catch {
    // Same fallback as readStoredAuth.
  }
}

function safeRemove(): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore — private mode etc.
  }
}

interface InternalState {
  status: AuthStatus;
  token: string | null;
  expiresAt: string | null;
  serviceReachable: boolean;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  // Synchronous initializer: read the stored auth AND eagerly
  // set the token on the API client during render, BEFORE any
  // child useEffect can fire a fetch. This is the load-bearing
  // line for the reload-logout bugfix — it must remain
  // synchronous.
  //
  // readStoredAuth (Pass 2.8) handles: missing entry, old plain-
  // string format (pre-2.8 → forced re-login), malformed JSON,
  // and already-expired tokens. We only get here with a parsed,
  // unexpired StoredAuth in hand.
  const [state, setState] = useState<InternalState>(() => {
    const stored = readStoredAuth();
    if (stored) {
      setApiToken(stored.token);
      return {
        status: "loading",
        token: stored.token,
        expiresAt: stored.expires_at,
        serviceReachable: true,
      };
    }
    return {
      status: "unauthenticated",
      token: null,
      expiresAt: null,
      serviceReachable: true,
    };
  });

  const logout = useCallback(() => {
    writeStoredAuth(null);
    setApiToken(null);
    setState({
      status: "unauthenticated",
      token: null,
      expiresAt: null,
      serviceReachable: true,
    });
  }, []);

  const login = useCallback((auth: StoredAuth) => {
    writeStoredAuth(auth);
    setApiToken(auth.token);
    setState({
      status: "authenticated",
      token: auth.token,
      expiresAt: auth.expires_at,
      serviceReachable: true,
    });
  }, []);

  // Validate the loading-state token. Runs once when status
  // transitions to "loading" (which only happens on initial
  // mount with a stored token).
  useEffect(() => {
    if (state.status !== "loading") return;
    const ctrl = new AbortController();
    listWorkspaces(ctrl.signal)
      .then(() => {
        if (ctrl.signal.aborted) return;
        setState((prev) =>
          prev.status === "loading"
            ? { ...prev, status: "authenticated", serviceReachable: true }
            : prev,
        );
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError && e.status === 401) {
          // Stale token — clear and route to login.
          writeStoredAuth(null);
          setApiToken(null);
          setState({
            status: "unauthenticated",
            token: null,
            expiresAt: null,
            serviceReachable: true,
          });
        } else if (e instanceof ApiError && e.status === 0) {
          // Network error — service unreachable. Optimistic auth:
          // we already had a valid-looking token in storage; let
          // the user proceed and try again. The serviceReachable
          // flag drives a banner. Don't punish them with re-login
          // for a transient connectivity blip.
          setState((prev) => ({
            ...prev,
            status: "authenticated",
            serviceReachable: false,
          }));
        } else {
          // Some other error — treat conservatively as unauthenticated.
          // (5xx from the service, malformed response, etc.)
          writeStoredAuth(null);
          setApiToken(null);
          setState({
            status: "unauthenticated",
            token: null,
            expiresAt: null,
            serviceReachable: true,
          });
        }
      });
    return () => ctrl.abort();
  }, [state.status]);

  // Wire up the 401 handler on mount, clear on unmount. Any
  // post-validation 401 (stale token, server-side rotation,
  // etc.) drops the user back to login automatically.
  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  const value = useMemo<AuthState>(() => {
    const claims = decodeJwtClaims(state.token);
    return {
      status: state.status,
      token: state.token,
      expiresAt: state.expiresAt,
      isAuthenticated: state.status === "authenticated",
      serviceReachable: state.serviceReachable,
      role: typeof claims?.role === "string" ? (claims.role as string) : null,
      userId:
        typeof claims?.user_id === "string" ? (claims.user_id as string) : null,
      login,
      logout,
    };
  }, [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
